import { useMemo } from 'react';
import { Card } from '#client/components/ui/Card';
import { CheckboxPicker } from '#client/components/ui/CheckboxPicker';
import { SortableSelection } from '#client/components/ui/SortableSelection';
import { useAllRecords } from '#client/hooks/useAllRecords';

interface Note {
  id: number;
  code: string;
  description: string;
}

interface ReportNotesCardProps {
  title: string;
  subtitle: string;
  /** Catalogue path the choices are loaded from. */
  path: string;
  /** Plural noun for the picker's placeholder and empty states. */
  noun: string;
  /** What is attached now, so its labels show even if outside the loaded page. */
  attached: readonly Note[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled: boolean;
}

/**
 * Picks catalogue notes (remarks or comments) for a report, and orders them.
 *
 * Ticking appends to the end; the list below is the print order, dragged into
 * place by staff.
 */
export function ReportNotesCard({
  title,
  subtitle,
  path,
  noun,
  attached,
  selectedIds,
  onChange,
  disabled,
}: ReportNotesCardProps) {
  const catalogue = useAllRecords<Note>({ path });
  const known = useMemo(() => {
    const byId = new Map(attached.map((note) => [note.id, note]));
    for (const note of catalogue.items) byId.set(note.id, note);
    return [...byId.values()];
  }, [attached, catalogue.items]);

  const label = (note: Note) => `${note.code} — ${note.description}`;

  return (
    <Card title={title} subtitle={subtitle}>
      <CheckboxPicker
        items={catalogue.items}
        selectedIds={selectedIds}
        onChange={onChange}
        label={label}
        searchText={(note) => note.description}
        loading={catalogue.loading}
        searchPlaceholder={`Search ${noun}`}
        emptyState={`No ${noun} defined yet`}
        disabled={disabled}
      />

      <p className="c-picker__order-label">Print order</p>
      <SortableSelection
        items={known}
        selectedIds={selectedIds}
        onChange={onChange}
        label={label}
        emptyState={`No ${noun} selected`}
        disabled={disabled}
      />
    </Card>
  );
}
